import { Field, Label } from '@headlessui/react';
import React, { FC, useCallback, useEffect } from 'react';
import {
  Control,
  useFieldArray,
  UseFormResetField,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import MinusIcon from '../../../../../../../assets/icons/minus-icon';
import { Checkbox } from '../../../../../../../components/checkbox/index';
import { Table } from '../../../../../../../components/table/index';
import { SharePlan } from '../../../../../../../hooks/index';
import { Valuation } from '../../../../../../../types/events.types';
import { VestingTask } from '../../../../../../../types/vesting-tasks.types';
import { toNumber } from '../../../../../../../utils/to-number.util';
import { FormSchema } from '../../exercise-form.validation';
import {
  ApproveHurdleColumn,
  approveHurdleColumnMap,
  approveHurdleColumnTranslationKey,
} from './hurdle.types';
import { HurdleTableRow } from './hurdle-table-row';

export type HurdleProps = {
  companyId: string;
  control: Control<FormSchema>;
  resetField: UseFormResetField<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
  valuation: Valuation;
};

export const Hurdle: FC<HurdleProps> = ({ control, companyId, setFormData, valuation }) => {
  const { stepOne } = useWatch<FormSchema>({ control });

  const { approveHurdleGrants: approveHurdleGrantsData } = SharePlan.useSharePlanGrantItems({
    companyId,
    sharePlanId: stepOne?.hurdle?.planId || '',
  });

  const approveHurdleGrants = approveHurdleGrantsData.filter(
    ({ vestedSharesCount, exercisedCount }) => vestedSharesCount - exercisedCount > 0,
  );

  const selectedGrantItemIds =
    stepOne?.hurdle?.hurdleItems
      ?.filter(({ selected }) => selected)
      .map(({ grantId }) => grantId) ?? [];

  const { fields: hurdleItemsFields, replace: replaceHurdleItems } = useFieldArray({
    control,
    name: 'stepOne.hurdle.hurdleItems',
  });

  // `hurdleItemsFields` is a snapshot taken at mount/replace: it does not carry the shares the user
  // typed into the row input. Writing a whole item back (useFieldArray `update`) would restore that
  // stale snapshot and wipe the custom amount, so selection and date write single fields instead.
  const setHurdleItemSelected = (hurdleItemsIndex: number, selected: boolean) => {
    setFormData(`stepOne.hurdle.hurdleItems.${hurdleItemsIndex}.selected`, selected, {
      shouldDirty: true,
    });
  };

  const setAllHurdleItemsSelected = (selected: boolean) => {
    hurdleItemsFields.forEach((_, index) => setHurdleItemSelected(index, selected));
  };

  const onDeselectAll = () => {
    setAllHurdleItemsSelected(false);
  };

  const onSelectAll = () => {
    setAllHurdleItemsSelected(true);
  };

  const handleSelectStakeholder = (hurdleItemsIndex: number) => {
    setHurdleItemSelected(hurdleItemsIndex, true);
  };
  const handleDeselectStakeholder = (hurdleItemsIndex: number) => {
    setHurdleItemSelected(hurdleItemsIndex, false);
  };

  const handlePrepareHurdleItem = useCallback(
    ({
      grantItem: {
        stakeholder,
        plan: {
          hardleValue,
          pool: { shareClass },
        },
      },
      id: grantId,
      vestedSharesCount,
      exercisedCount,
    }: VestingTask) => {
      const exercisableShares = vestedSharesCount - exercisedCount;

      return {
        stakeholder,
        grantId,
        selected: true,
        hurdleValue: toNumber(hardleValue),
        shareClassName: shareClass.name,
        issuedShares: exercisableShares,
        vestedSharesCount,
        exercisedCount,
        date: new Date(),
      };
    },
    [],
  );

  const calculateBalance = useCallback(
    (index: number) => {
      const item = stepOne?.hurdle?.hurdleItems?.[index];
      const vestedShares = item?.vestedSharesCount || 0;
      const exercisedShares = item?.exercisedCount || 0;
      const value = item?.issuedShares || 0;
      const balance = Math.max(vestedShares - exercisedShares - value, 0);

      return balance.toLocaleString('en-US');
    },
    [stepOne?.hurdle?.hurdleItems],
  );

  useEffect(() => {
    if (approveHurdleGrants.length === hurdleItemsFields.length) return;
    replaceHurdleItems(approveHurdleGrants.map(handlePrepareHurdleItem));
  }, [approveHurdleGrants, handlePrepareHurdleItem, hurdleItemsFields.length, replaceHurdleItems]);

  return (
    <>
      <div className="invisible-y-scroll max-h-[400px] w-[1309px] rounded-lg border-[1px] border-gray-100">
        <Table
          columnOrder={approveHurdleColumnMap}
          columnsTitle={{
            ...approveHurdleColumnTranslationKey,
            [ApproveHurdleColumn.SELECT]: (
              <Field className="flex cursor-pointer items-center">
                <Checkbox
                  checked={selectedGrantItemIds.length > 0}
                  id="select-all-hurdle-plans"
                  onChange={(checked) => (checked ? onSelectAll() : onDeselectAll())}
                  selectIcon={<MinusIcon />}
                />
                <Label
                  className="absolute left-0 top-0 h-full w-full cursor-pointer"
                  htmlFor="select-all-hurdle-plans"
                />
              </Field>
            ),
          }}
          tHeadClassNames={{
            thClassName: 'pl-4',
            labelClassName: 'font-medium whitespace-nowrap text-[#172335] text-label-md',
          }}
        >
          {hurdleItemsFields.map((approveItem, i) => {
            const {
              hurdleValue,
              grantId,
              shareClassName,
              stakeholder: { fullName },
            } = approveItem;
            const displayGrantId =
              approveHurdleGrants.find(({ id }) => id === grantId)?.grantItem.id ?? grantId;

            return (
              <HurdleTableRow
                availableBalance={calculateBalance(i)}
                control={control}
                currentValuation={
                  toNumber(valuation.issuedShares || valuation.issuedSharesOnStart) *
                  valuation.sharePrice
                }
                displayGrantId={displayGrantId}
                grantId={grantId}
                hurdleValue={hurdleValue}
                id={grantId}
                isSelected={selectedGrantItemIds.includes(grantId)}
                key={grantId}
                onDateChange={(date) =>
                  setFormData(`stepOne.hurdle.hurdleItems.${i}.date`, date, { shouldDirty: true })
                }
                onDeselect={() => handleDeselectStakeholder(i)}
                onSelect={() => handleSelectStakeholder(i)}
                rowIndex={i}
                shareClassName={shareClassName}
                stakeholderName={fullName}
              />
            );
          })}
        </Table>
      </div>
    </>
  );
};
